// @ts-nocheck
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Youtube, Instagram, Facebook, Twitter, Bell, Settings, LayoutDashboard, LogOut, BellRing, CreditCard, Key } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useState } from "react";
import { RecruitLookLogo } from "@/components/ui/logo";
import { PublicEventLogo } from "@/components/public/PublicEventLogo";
import { useAuth } from "@/contexts/AuthContext";
import { useUserDashboards } from "@/hooks/useUserDashboards";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import NotificationDropdown from "../dashboard/NotificationDropdown";
import UserNotificationSettingsModal from "../common/UserNotificationSettingsModal";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

export function PublicHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  
  // Safely access auth context - may not be available during initial render
  let user = null;
  let profile = null;
  let signOut = async () => {};
  
  try {
    const authContext = useAuth();
    user = authContext.user;
    profile = authContext.profile;
    signOut = authContext.signOut;
  } catch (error) {
    // AuthProvider not yet initialized - render header without user
    console.warn('PublicHeader: AuthProvider not yet initialized');
  }
  
  const { dashboards, isLoading: dashboardsLoading } = useUserDashboards();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.email) {
      toast.error('Email not found');
      return;
    }
    
    try {
      const { error } = await invokeEdgeFunction('send-password-reset', {
        body: { email: user.email }
      });
      
      if (error) throw error;
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Error sending reset email:', error);
      toast.error('Failed to send password reset email');
    }
  };

  const getUserDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name) {
      return profile.first_name;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const getUserInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    if (profile?.first_name) {
      return profile.first_name[0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const getMembershipBadge = () => {
    if (!profile?.active_membership_slug) return 'Free';
    
    // Format the membership slug into a readable name
    const membershipName = profile.active_membership_slug
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return membershipName;
  };

  return (
    <>
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-10 text-sm font-bold">
            <div className="flex items-center space-x-3">
              <a
                href="https://www.instagram.com/recruit_look/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/RecruitLook/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://x.com/RecruitLook"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="https://www.youtube.com/channel/UCMF2lb0EItdz1ENq--u3DnQ"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
            <div className="flex items-center text-xs">
              <a
                href="https://www.recruitlook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-secondary transition-colors font-black tracking-tight"
              >
                ABOUT RECRUITLOOK.COM
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="fixed top-10 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <PublicEventLogo className="ml-2" />
            </Link>

            {/* Right side - Navigation and User Menu grouped together */}
            <div className="flex items-center space-x-8">
              <nav className="hidden lg:flex items-center space-x-8" />


              {/* Auth-aware CTA Buttons / User Menu */}
              <div className="flex items-center space-x-4">
              {user ? (
                // Authenticated user controls
                <>
                  {/* Notifications */}
                  <NotificationDropdown />

                  {/* User dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={profile?.avatar_url || ''} alt={getUserDisplayName()} />
                          <AvatarFallback className="bg-primary text-white">
                            {getUserInitials()}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-80" align="end" forceMount>
                      {/* User Info Section */}
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-2">
                          <p className="text-sm font-medium leading-none">{getUserDisplayName()}</p>
                          <p className="text-xs leading-none text-muted-foreground">
                            {user.email}
                          </p>
                          <Badge variant="secondary" className="w-fit text-xs">
                            {getMembershipBadge()}
                          </Badge>
                        </div>
                      </DropdownMenuLabel>
                      
                      <DropdownMenuSeparator />
                      
                      {/* Dashboard Links */}
                      {(!dashboardsLoading && dashboards.length > 0) || profile?.role === 'athlete' ? (
                        <>
                          <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">
                            My Dashboards
                          </DropdownMenuLabel>
                          
                          {/* Always show Athlete Dashboard for athletes */}
                          {profile?.role === 'athlete' && (
                            <DropdownMenuItem
                              onClick={() => navigate('/athlete-dashboard')}
                              className="cursor-pointer"
                            >
                              <LayoutDashboard className="mr-2 h-4 w-4" />
                              <span>Athlete Dashboard</span>
                            </DropdownMenuItem>
                          )}
                          
                          {/* Show other dashboards from role assignments */}
                          {dashboards.filter(d => d.role_slug !== 'athlete').map((dashboard) => (
                            <DropdownMenuItem
                              key={dashboard.role_slug}
                              onClick={() => navigate(dashboard.dashboard_path)}
                              className="cursor-pointer"
                            >
                              <LayoutDashboard className="mr-2 h-4 w-4" />
                              <span>{dashboard.role_name} Dashboard</span>
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                        </>
                      ) : null}

                       {/* Preferences (athlete only) */}
                       {profile?.role === 'athlete' && (
                         <DropdownMenuItem onClick={() => navigate('/athlete-dashboard/preferences')} className="cursor-pointer">
                           <Settings className="mr-2 h-4 w-4" />
                           <span>Preferences</span>
                         </DropdownMenuItem>
                       )}

                       {/* Notification Settings */}
                       <DropdownMenuItem onClick={() => setNotificationSettingsOpen(true)} className="cursor-pointer">
                         <BellRing className="mr-2 h-4 w-4" />
                         <span>Notification Settings</span>
                       </DropdownMenuItem>

                        {/* Billing & Subscriptions */}
                        <DropdownMenuItem onClick={() => {
                          console.log('🔍 PublicHeader: Billing & Subscriptions clicked - navigating to /billing');
                          console.log('🔍 PublicHeader: Current user:', user?.id);
                          console.log('🔍 PublicHeader: Current profile:', profile?.id);
                          navigate('/billing');
                        }} className="cursor-pointer">
                          <CreditCard className="mr-2 h-4 w-4" />
                          <span>Billing & Subscriptions</span>
                        </DropdownMenuItem>

                      <DropdownMenuSeparator />

                      {/* Reset Password */}
                      <DropdownMenuItem onClick={handleResetPassword} className="cursor-pointer">
                        <Key className="mr-2 h-4 w-4" />
                        <span>Reset Password</span>
                      </DropdownMenuItem>

                      {/* Sign Out */}
                      <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600">
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Sign Out</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                // Non-authenticated user controls
                <>
                  <Link to="/login">
                    <Button variant="outline" className="border-secondary text-slate-900 hover:bg-secondary/10 font-black px-6 py-3">
                      LOGIN
                    </Button>
                  </Link>
                  <Link to="/request-info">
                    <Button className="bg-secondary hover:bg-secondary/90 text-slate-900 font-black px-6 py-3">
                      REQUEST INFO
                    </Button>
                  </Link>
                </>
              )}
              </div>

              {/* Mobile menu button */}
              <button
                className="lg:hidden"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6 text-slate-900" />
                ) : (
                  <Menu className="w-6 h-6 text-slate-900" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden py-6 border-t border-slate-200 bg-white/95">
              <div className="flex flex-col space-y-6">
                <div className="pt-4 space-y-3">
                  {user ? (
                    <div className="flex items-center space-x-3 px-4">
                      <NotificationDropdown />
                      <div className="flex items-center space-x-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={profile?.avatar_url || ''} alt={getUserDisplayName()} />
                          <AvatarFallback className="bg-primary text-white text-xs">
                            {getUserInitials()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-semibold text-slate-900">{getUserDisplayName()}</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                        <Button variant="outline" className="w-full border-secondary text-slate-900 hover:bg-secondary/10 font-black">
                          LOGIN
                        </Button>
                      </Link>
                      <Link to="/request-info" onClick={() => setIsMenuOpen(false)}>
                        <Button className="w-full bg-secondary hover:bg-secondary/90 text-slate-900 font-black">
                          REQUEST INFO
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* User Notification Settings Modal */}
      <UserNotificationSettingsModal 
        isOpen={notificationSettingsOpen}
        onClose={() => setNotificationSettingsOpen(false)}
      />
    </>
  );
}